
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorkStyle } from '../types';
import { isHomeBasedWorkStyle } from '../src/utils/workStyleUtils';
import { useCities } from '../src/contexts/CitiesContext';

interface GlobalOfficesProps {
  onSelectCity: (city: { id: string; name: string; enName: string }) => void;
  onBack: () => void;
}

const GlobalOffices: React.FC<GlobalOfficesProps> = ({ onSelectCity, onBack }) => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const [selectedWorkStyle, setSelectedWorkStyle] = useState<WorkStyle | 'all'>('all');
  const { cities, cityJobCounts, loading } = useCities();

  // 根据筛选条件过滤城市列表，并排序
  const filteredCities = useMemo(() => {
    let filtered = cities;

    if (selectedWorkStyle === 'all') {
      // 不过滤
    } else if (isHomeBasedWorkStyle(selectedWorkStyle)) {
      filtered = cities.filter(city => city.isRemoteOnly);
    } else {
      filtered = cities.filter(city => !city.isRemoteOnly);
    }

    return [...filtered].sort((a, b) => {
      if (a.isRemoteOnly !== b.isRemoteOnly) {
        return a.isRemoteOnly ? 1 : -1;
      }
      return (cityJobCounts[b.id] || 0) - (cityJobCounts[a.id] || 0);
    });
  }, [selectedWorkStyle, cities, cityJobCounts]);

  // 统计每种 workStyle 的城市数量
  const citiesCount = useMemo(() => {
    const onsiteCities = cities.filter(city => !city.isRemoteOnly);
    const homebasedCities = cities.filter(city => city.isRemoteOnly);
    return {
      all: cities.length,
      onsite: onsiteCities.length,
      homebased: homebasedCities.length,
    };
  }, [cities]);

  return (
    <div className="pt-16 pb-16 sm:pb-24 md:pb-32 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12">
        {/* Header Section */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.654M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{t('globalOffices.title')}</h1>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">{t('globalOffices.subtitle')}</p>
            </div>
          </div>

          {/* Work Style Filter Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => setSelectedWorkStyle('all')}
              className={`inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                selectedWorkStyle === 'all'
                  ? 'bg-slate-900 text-white shadow-lg shadow-slate-500/25'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-white'
              }`}
            >
              <span className="w-5 h-5">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.654M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <span>{t('globalOffices.filterAll')}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                selectedWorkStyle === 'all' ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
              }`}>
                {citiesCount.all}
              </span>
            </button>

            <button
              onClick={() => setSelectedWorkStyle('ONSITE')}
              className={`inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                selectedWorkStyle === 'ONSITE'
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-500 hover:text-indigo-600 hover:bg-white'
              }`}
            >
              <span className="w-5 h-5">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              <span>{t('globalOffices.filterOnsite')}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                selectedWorkStyle === 'ONSITE' ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
              }`}>
                {citiesCount.onsite}
              </span>
            </button>

            <button
              onClick={() => setSelectedWorkStyle('HOME_BASED')}
              className={`inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                isHomeBasedWorkStyle(selectedWorkStyle)
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 hover:bg-white'
              }`}
            >
              <span className="w-5 h-5">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </span>
              <span>{t('globalOffices.filterHomeBased')}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isHomeBasedWorkStyle(selectedWorkStyle) ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
              }`}>
                {citiesCount.homebased}
              </span>
            </button>
          </div>
        </div>

        {/* 城市网格列表 */}
        <div>
          {/* 筛选结果统计 */}
          <div className="mb-8">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              {selectedWorkStyle === 'all' ? t('globalOffices.officeHubs', { count: filteredCities.length }) : selectedWorkStyle === 'ONSITE' ? t('globalOffices.onsiteOfficeHubs', { count: filteredCities.length }) : isHomeBasedWorkStyle(selectedWorkStyle) ? t('globalOffices.homeBasedOfficeHubs', { count: filteredCities.length }) : t('globalOffices.officeHubs', { count: filteredCities.length })}
            </h2>
          </div>

          {/* 单层网格布局 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-6 gap-4">
            {filteredCities.map(city => (
              <Link
                key={city.id}
                to={`/cities/${city.id}`}
                id={`city-node-${city.id}`}
                className={`group bg-white rounded-xl border p-2 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 cursor-pointer relative ${
                  city.isRemoteOnly ? 'border-green-400 hover:border-green-500' : 'border-slate-200 hover:border-blue-500'
                }`}
              >
                {/* Remote Hub Badge */}
                {city.isRemoteOnly && (
                  <div className="absolute top-3 right-3 z-10 bg-green-500 text-white text-[8px] sm:text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider shadow-lg">
                    {t('cityDetail.remoteHub')}
                  </div>
                )}

                <div className="relative h-32 rounded-xl overflow-hidden mb-3">
                  <img
                    src={city.image}
                    alt={city.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 brightness-90 group-hover:brightness-100"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-white/80 uppercase tracking-tighter">
                        {isZh ? (city.country?.nameZh || city.countryCode) : (city.country?.nameEn || city.countryCode)}
                      </span>
                      <div className={`${city.isRemoteOnly ? 'bg-green-500/30' : 'bg-white/20'} backdrop-blur-md px-1.5 py-0.5 rounded text-[9px] font-black text-white`}>
                        {cityJobCounts[city.id] || 0}P
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-1.5 pb-2">
                  <h3 className={`text-sm font-black truncate group-hover:text-blue-600 transition-colors ${
                    city.isRemoteOnly ? 'text-green-600 group-hover:text-green-700' : 'text-slate-900'
                  }`}>
                    {isZh ? city.nameZh : city.nameEn}
                  </h3>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate mt-0.5">
                    {isZh ? city.nameEn : city.nameZh}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* 空状态 */}
          {filteredCities.length === 0 && (
            <div className="py-40 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-slate-50 rounded-full flex items-center justify-center">
                {selectedWorkStyle === 'ONSITE' ? (
                  <svg className="w-10 h-10 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ) : isHomeBasedWorkStyle(selectedWorkStyle) ? (
                  <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                ) : (
                  <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 002 2 2 2 0 012 2v.654M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <h3 className="text-2xl font-black text-slate-900">{t('globalOffices.noOffices')}</h3>
              <p className="text-slate-500 font-medium mt-2">
                {selectedWorkStyle === 'ONSITE' ? t('globalOffices.noOfficesDesc', { type: t('globalOffices.filterOnsite').toLowerCase() }) :
                 isHomeBasedWorkStyle(selectedWorkStyle) ? t('globalOffices.noOfficesDesc', { type: t('globalOffices.filterHomeBased').toLowerCase() }) :
                 t('globalOffices.noOfficesDesc', { type: t('globalOffices.filterAll').toLowerCase() })}
              </p>
              <button
                onClick={() => setSelectedWorkStyle('all')}
                className="mt-8 bg-slate-900 text-white px-8 py-3 rounded-xl font-bold text-sm hover:bg-slate-800 active:scale-95 transition-all cursor-pointer"
              >
                {t('globalOffices.showAllOffices')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalOffices;
